interface Props {
  color: string;
  label: string;
  className?: string;
}

// 1976 BBQ bottle silhouette: white screw cap, short narrow neck,
// curved shoulder flaring out to a wide cylindrical body that runs
// straight to a flat-ish bottom. A cream paper label sits on the
// lower half of the body with "1976" + the sauce name printed on it,
// mirroring the real product.
export function BottleIcon({ color, label, className }: Props) {
  // viewBox 60 x 100. Body x=8..52 (width 44), neck x=22..38 (width 16),
  // cap x=18..42 (width 24).
  const bodyPath =
    "M 22 14 " +
    "L 22 22 " + // narrow neck
    "C 22 27, 8 30, 8 38 " + // shoulder curves out to body width
    "L 8 95 " + // straight cylindrical body
    "C 8 97, 10 98, 12 98 " + // small bottom-left radius
    "L 48 98 " +
    "C 50 98, 52 97, 52 95 " + // small bottom-right radius
    "L 52 38 " +
    "C 52 30, 38 27, 38 22 " + // shoulder on the right back to neck
    "L 38 14 Z";

  // Label widens to the bottle's edge so it actually wraps the body.
  const labelX = 8.5;
  const labelW = 43;
  const labelY = 56;
  const labelH = 32;
  const cx = labelX + labelW / 2;

  // Pick a font size that keeps the longest sauce names inside the label.
  const sauceFontSize = label.length > 9 ? 5 : label.length > 6 ? 6 : 7;

  return (
    <svg viewBox="0 0 60 100" className={className} role="img" aria-hidden="true" focusable="false">
      <title>{label} sauce bottle</title>
      <defs>
        <linearGradient id="bottleSheen" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="rgba(255,255,255,0.22)" />
          <stop offset="45%" stopColor="rgba(255,255,255,0)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.45)" />
        </linearGradient>
        <linearGradient id="capSheen" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="rgba(220,212,196,1)" />
          <stop offset="45%" stopColor="rgba(255,250,238,1)" />
          <stop offset="100%" stopColor="rgba(150,140,120,1)" />
        </linearGradient>
      </defs>

      {/* Sauce-colored body */}
      <path
        d={bodyPath}
        fill={color}
        stroke="rgba(0,0,0,0.6)"
        strokeWidth="0.7"
        strokeLinejoin="round"
      />

      {/* Glass sheen across body */}
      <path d={bodyPath} fill="url(#bottleSheen)" />

      {/* Cream paper label */}
      <rect
        x={labelX}
        y={labelY}
        width={labelW}
        height={labelH}
        fill="#f5e9d4"
        stroke="rgba(0,0,0,0.35)"
        strokeWidth="0.4"
      />
      {/* Subtle inner shadow on the label so it sits on the bottle */}
      <rect
        x={labelX}
        y={labelY}
        width={labelW}
        height={labelH}
        fill="url(#bottleSheen)"
        opacity="0.7"
      />

      {/* "1976" in brand red */}
      <text
        x={cx}
        y={labelY + 10}
        textAnchor="middle"
        fontFamily="'Bebas Neue', 'Anton', 'Oswald', sans-serif"
        fontWeight="700"
        fontSize="10"
        fill="#b54e1e"
      >
        1976
      </text>
      {/* Thin separator under the wordmark */}
      <line
        x1={labelX + 4}
        y1={labelY + 13}
        x2={labelX + labelW - 4}
        y2={labelY + 13}
        stroke="rgba(22,18,16,0.55)"
        strokeWidth="0.5"
      />
      {/* Sauce name */}
      <text
        x={cx}
        y={labelY + 13 + sauceFontSize + 3}
        textAnchor="middle"
        fontFamily="'Bebas Neue', 'Anton', 'Oswald', sans-serif"
        fontWeight="700"
        fontSize={sauceFontSize}
        fill="#161210"
        letterSpacing="0.3"
      >
        {label.toUpperCase()}
      </text>
      {/* "SAUCE" mini-line */}
      <text
        x={cx}
        y={labelY + labelH - 3}
        textAnchor="middle"
        fontFamily="'Inter', system-ui, sans-serif"
        fontWeight="600"
        fontSize="3.2"
        fill="#161210"
        letterSpacing="0.6"
      >
        SAUCE
      </text>

      {/* White screw cap (wider than the neck) */}
      <rect
        x="18"
        y="2"
        width="24"
        height="12"
        rx="1.2"
        fill="url(#capSheen)"
        stroke="rgba(0,0,0,0.45)"
        strokeWidth="0.6"
      />
      {/* Cap underside shadow where it meets the neck */}
      <rect x="18" y="12" width="24" height="2" fill="rgba(0,0,0,0.28)" />
      {/* Faint screw ridges */}
      <line x1="20" y1="5" x2="40" y2="5" stroke="rgba(0,0,0,0.18)" strokeWidth="0.4" />
      <line x1="20" y1="8" x2="40" y2="8" stroke="rgba(0,0,0,0.18)" strokeWidth="0.4" />
      <line x1="20" y1="11" x2="40" y2="11" stroke="rgba(0,0,0,0.18)" strokeWidth="0.4" />
    </svg>
  );
}
